import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as opCardOwnershipService from '../services/opCardOwnershipService';
import type { OnePieceCardOwnershipInput } from '../services/opCardOwnershipService';
import { useAuth } from '../contexts/AuthContext';

export function useOnePieceCardOwnerships(
  setId?: string,
  includePricing: boolean = true,
  source: 'master' | 'collection' = 'master'
) {
  const { isAuthenticated, user } = useAuth();

  return useQuery({
    queryKey: ['onePieceCardOwnerships', setId, includePricing, source],
    queryFn: async () => {
      console.log(
        '[useOnePieceCardOwnerships] Fetching ownerships for setId:',
        setId,
        'includePricing:',
        includePricing
      );
      console.log('[useOnePieceCardOwnerships] Auth state:', {
        isAuthenticated,
        userId: user?.userId,
      });

      if (!isAuthenticated) {
        console.warn(
          '[useOnePieceCardOwnerships] Not authenticated, returning empty array',
        );
        return [];
      }

      const result =
        await opCardOwnershipService.getUserOnePieceCardOwnerships(setId, includePricing, source);
      console.log(
        '[useOnePieceCardOwnerships] Fetched',
        result.length,
        'ownerships',
      );
      return result;
    },
    staleTime: 0, // Always fetch fresh data after mutations
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (v4 uses cacheTime, not gcTime)
    retry: 1,
    enabled: isAuthenticated,
    refetchOnMount: 'always', // Always refetch to see latest ownership changes
    refetchOnWindowFocus: false, // Disabled to prevent unexpected refetches when interacting with dev tools
  });
}

export function useOnePieceSetProgress() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['onePieceSetProgress'],
    queryFn: () => opCardOwnershipService.getUserOnePieceSetProgress(),
    staleTime: 0,
    retry: 1,
    enabled: isAuthenticated,
  });
}

export function useOnePieceCardOwnership(cardId: string) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['onePieceCardOwnership', cardId],
    queryFn: () => opCardOwnershipService.getOnePieceCardOwnership(cardId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: isAuthenticated && !!cardId,
  });
}

export function useUpdateOnePieceCardOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      data,
    }: {
      cardId: string;
      data: OnePieceCardOwnershipInput;
    }) => opCardOwnershipService.upsertOnePieceCardOwnership(cardId, data),
    onSuccess: (data, variables) => {
      console.log(
        '[UpdateOwnership] Success - cardId:',
        variables.cardId,
        'data:',
        data,
      );

      // Update individual card cache
      queryClient.setQueryData(
        ['onePieceCardOwnership', variables.cardId],
        data,
      );

      // Update ALL ownership lists (not just specific setId)
      queryClient.setQueriesData<
        import('../services/opCardOwnershipService').OnePieceCardOwnership[]
      >({ queryKey: ['onePieceCardOwnerships'] }, (old) => {
        if (!old) return [data];
        const exists = old.find((o) => o.cardId === variables.cardId);
        if (exists) {
          // Update existing entry
          return old.map((o) => (o.cardId === variables.cardId ? data : o));
        }
        // Add new entry
        return [...old, data];
      });

      // Invalidate derived data
      queryClient.invalidateQueries({ queryKey: ['onePieceSetProgress'] });
      queryClient.invalidateQueries({ queryKey: ['card-locations'] }); // Refresh location suggestions

      console.log('[UpdateOwnership] Queries updated');
    },
  });
}

export function useToggleOnePieceCardOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, setId, justTcgId }: { cardId: string; setId: string; justTcgId?: string }) =>
      opCardOwnershipService.toggleOnePieceCardOwnership(cardId, setId, justTcgId),
    // Optimistic update - update UI immediately before API call completes
    onMutate: async ({ cardId, setId, justTcgId }) => {
      console.log('[Toggle] onMutate - cardId:', cardId, 'setId:', setId, 'justTcgId:', justTcgId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['onePieceCardOwnerships'] });
      await queryClient.cancelQueries({
        queryKey: ['onePieceCardOwnership', cardId],
      });

      // Snapshot the previous value
      const previousOwnerships = queryClient.getQueriesData({
        queryKey: ['onePieceCardOwnerships'],
      });
      const previousOwnership = queryClient.getQueryData([
        'onePieceCardOwnership',
        cardId,
      ]);

      // Optimistically update to the new value for all ownership lists
      queryClient.setQueriesData<
        import('../services/opCardOwnershipService').OnePieceCardOwnership[]
      >({ queryKey: ['onePieceCardOwnerships'] }, (old) => {
        if (!old) return old;

        const existing = old.find((o) => o.cardId === cardId);
        if (existing) {
          // Toggle existing
          return old.map((o) =>
            o.cardId === cardId
              ? { ...o, owned: !o.owned, quantity: !o.owned ? 1 : 0, inCollection: o.inCollection ?? false }
              : o,
          );
        } else {
          // Add new owned card - use the provided setId
          return [
            ...old,
            {
              userId: '', // Will be filled by backend
              cardId,
              setId, // Use the setId from the parameter, not derived from cardId
              justTcgId,
              owned: true,
              quantity: 1,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ];
        }
      });

      return { previousOwnerships, previousOwnership };
    },
    onSuccess: (data, { cardId }) => {
      console.log('[Toggle] Success - cardId:', cardId, 'data:', data);

      // Update with real data from server
      queryClient.setQueryData(['onePieceCardOwnership', cardId], data);

      // Update all ownership lists with the real data
      queryClient.setQueriesData<
        import('../services/opCardOwnershipService').OnePieceCardOwnership[]
      >({ queryKey: ['onePieceCardOwnerships'] }, (old) => {
        if (!old) return old;
        const exists = old.find((o) => o.cardId === cardId);
        if (exists) {
          return old.map((o) => (o.cardId === cardId ? data : o));
        }
        return [...old, data];
      });

      // Invalidate set progress and locations
      queryClient.invalidateQueries({ queryKey: ['onePieceSetProgress'] });
      queryClient.invalidateQueries({ queryKey: ['card-locations'] });

      console.log('[Toggle] Queries updated');
    },
    onError: (error, { cardId }, context) => {
      console.error('[Toggle] Error - cardId:', cardId, 'error:', error);

      // Rollback optimistic updates
      if (context?.previousOwnerships) {
        context.previousOwnerships.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousOwnership) {
        queryClient.setQueryData(
          ['onePieceCardOwnership', cardId],
          context.previousOwnership,
        );
      }
    },
  });
}

/**
 * Hook to toggle card wishlist status
 */
export function useToggleOnePieceCardWishlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, setId }: { cardId: string; setId: string }) =>
      opCardOwnershipService.toggleOnePieceCardWishlist(cardId, setId),
    onSuccess: (data, { cardId, setId }) => {
      // Update both the list and single card cache
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnerships', setId] });
      queryClient.setQueryData(['onePieceCardOwnership', cardId], data);
    },
  });
}

/**
 * Hook to toggle card favorite status
 */
export function useToggleOnePieceCardFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cardId, setId }: { cardId: string; setId: string }) =>
      opCardOwnershipService.toggleOnePieceCardFavorite(cardId, setId),
    onSuccess: (data, { cardId, setId }) => {
      // Update both the list and single card cache
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnerships', setId] });
      queryClient.setQueryData(['onePieceCardOwnership', cardId], data);
    },
  });
}

export function useDeleteOnePieceCardOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) =>
      opCardOwnershipService.deleteOnePieceCardOwnership(cardId),
    onSuccess: (_, cardId) => {
      console.log('[DeleteOwnership] Success - cardId:', cardId);

      // Remove from individual card cache
      queryClient.setQueryData(['onePieceCardOwnership', cardId], null);

      // Remove from ALL ownership lists
      queryClient.setQueriesData<
        import('../services/opCardOwnershipService').OnePieceCardOwnership[]
      >({ queryKey: ['onePieceCardOwnerships'] }, (old) => {
        if (!old) return old;
        return old.filter((o) => o.cardId !== cardId);
      });

      // Invalidate derived data
      queryClient.invalidateQueries({ queryKey: ['onePieceSetProgress'] });
      queryClient.invalidateQueries({ queryKey: ['card-locations'] });

      console.log('[DeleteOwnership] Queries updated');
    },
  });
}

export function useBulkUpdateOnePieceCardOwnerships() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      ownerships: Array<{ cardId: string } & OnePieceCardOwnershipInput>,
    ) => opCardOwnershipService.bulkUpsertOnePieceCardOwnerships(ownerships),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnerships'] });
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnership'] });
      queryClient.invalidateQueries({ queryKey: ['onePieceSetProgress'] });
      queryClient.invalidateQueries({ queryKey: ['card-locations'] }); // Refresh location suggestions
    },
  });
}

export function useOnePieceCardOwnershipsMap(setId?: string, source: 'master' | 'collection' = 'master') {
  const { data: ownerships = [], ...rest } = useOnePieceCardOwnerships(setId, true, source);

  const ownershipsMap = React.useMemo(() => {
    return opCardOwnershipService.onePieceOwnershipsToMap(
      ownerships as opCardOwnershipService.OnePieceCardOwnership[],
    );
  }, [ownerships]);

  return {
    ownershipsMap,
    ownerships,
    ...rest,
  };
}

export function useSyncOnePieceProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => opCardOwnershipService.syncOnePieceSetProgress(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onePieceSetProgress'] });
      queryClient.invalidateQueries({ queryKey: ['onePieceCardOwnerships'] });
    },
  });
}
